import { HttpErrorResponse } from '@angular/common/http';

/**
 * Un mensaje listo para el traductor: la clave es el texto en español, como en
 * todo el diccionario, y `params` completa lo que cambia de vez en vez.
 *
 * Viaja así y no como texto ya armado porque el login lo pintaba tal cual, sin
 * pasarlo por el traductor: en inglés, francés o portugués el error salía
 * igual en español. Armarlo acá con los segundos adentro haría imposible
 * traducirlo, porque cada valor sería una clave distinta.
 */
export interface MensajeTraducible {
  clave: string;
  params?: Record<string, unknown>;
}

export const MENSAJES_DE_LOGIN = {
  credencialesInvalidas: 'Usuario o contraseña inválidos',
  sinAcceso: 'No tienes acceso al módulo de Mantenimiento',
  cuentaBloqueada: 'Demasiados intentos con esta cuenta. Espera unos minutos o recupera tu contraseña.',
  sinConexion: 'No hay conexión. Revisa tu red e intenta de nuevo.',
  esperarSegundos: 'Demasiados intentos. Espera {{segundos}} segundos e intenta de nuevo.',
  esperarUnMinuto: 'Demasiados intentos. Espera un minuto e intenta de nuevo.',
  servicioNoDisponible: 'El servicio no está disponible en este momento. Intenta de nuevo en unos minutos.',
  desconocido: 'No se pudo iniciar sesión. Intenta de nuevo.',
} as const;

/** Lo que Firebase responde cuando el correo o la contraseña no corresponden. */
const CODIGOS_DE_CREDENCIALES = new Set([
  'auth/invalid-credential',
  'auth/wrong-password',
  'auth/user-not-found',
  'auth/invalid-email',
  'auth/user-disabled',
]);

const mensaje = (clave: string, params?: Record<string, unknown>): MensajeTraducible =>
  params ? { clave, params } : { clave };

/**
 * Qué decirle a quien no pudo iniciar sesión.
 *
 * Antes cualquier error salía como "Usuario o contraseña inválidos". En SIT,
 * Console limitaba las peticiones por exceso y la gente leía que su contraseña
 * estaba mal: reintentaba, y cada reintento estiraba el bloqueo. Lo que hay que
 * hacer es distinto en cada caso —corregir la contraseña, esperar, o avisar que
 * el servicio no responde—, así que el mensaje tiene que decir cuál es.
 *
 * Ante un error que no se reconoce no se acusa a la contraseña: era justamente
 * la mentira que hacía reintentar.
 */
export function mensajeDeErrorDeLogin(error: unknown): MensajeTraducible {
  const codigo = (error as { code?: unknown })?.code;
  if (typeof codigo === 'string' && codigo.startsWith('auth/')) {
    if (codigo === 'auth/too-many-requests') return mensaje(MENSAJES_DE_LOGIN.cuentaBloqueada);
    if (codigo === 'auth/network-request-failed') return mensaje(MENSAJES_DE_LOGIN.sinConexion);
    if (CODIGOS_DE_CREDENCIALES.has(codigo)) return mensaje(MENSAJES_DE_LOGIN.credencialesInvalidas);
  }

  if ((error as Error)?.message === 'ACCESS_DENIED') return mensaje(MENSAJES_DE_LOGIN.sinAcceso);

  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const segundos = Number((error.error as { retryAfterSeconds?: unknown } | null)?.retryAfterSeconds);
      return Number.isFinite(segundos) && segundos > 0
        ? mensaje(MENSAJES_DE_LOGIN.esperarSegundos, { segundos })
        : mensaje(MENSAJES_DE_LOGIN.esperarUnMinuto);
    }
    if (error.status === 0 || error.status >= 500) return mensaje(MENSAJES_DE_LOGIN.servicioNoDisponible);
    if (error.status === 401) return mensaje(MENSAJES_DE_LOGIN.credencialesInvalidas);
    if (error.status === 403) return mensaje(MENSAJES_DE_LOGIN.sinAcceso);
  }

  return mensaje(MENSAJES_DE_LOGIN.desconocido);
}
